import type { MeasureInput, Rect } from './surfaces';
import { discoverSurfaces, invalidateDiscovery } from './discover-surfaces';

export const COMPANION_SELF_ATTR = 'data-companion-self';

export const COMPANION_SLOTS = {
  content: '.nula-center',
  perches: [
    '[data-slot="companion-perch-tabbar"]',
    '[data-slot="companion-perch-assistant"]',
  ],
  sidebar: '[data-slot="companion-wall-sidebar"]',
  /** The perch the companion gravitates back to when it is on screen. */
  favoured: '[data-slot="companion-perch-assistant"]',
} as const;

export const COMPANION_SURFACE_ATTR = 'data-companion-surface';

function rectOf(el: Element): Rect | null {
  const r = el.getBoundingClientRect();
  if (r.width <= 0 || r.height <= 0) return null;
  return { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
}

/**
 * Snapshot the live chrome the companion walks on. Returns plain rects so the
 * geometry layer stays pure and testable — and so this feature never has to
 * import from `layout/`, which the layer boundaries forbid.
 */
export function readWorld(petWidth: number, now: number = Date.now()): MeasureInput | null {
  const centerEl = document.querySelector(COMPANION_SLOTS.content);
  if (!centerEl) return null;

  const content = rectOf(centerEl);
  if (!content) return null;

  const perches: Rect[] = [];
  let favoured: { left: number; right: number } | null = null;
  for (const selector of COMPANION_SLOTS.perches) {
    const el = document.querySelector(selector);
    const rect = el ? rectOf(el) : null;
    if (!rect) continue;
    perches.push(rect);
    if (selector === COMPANION_SLOTS.favoured) {
      favoured = { left: rect.left, right: rect.right };
    }
  }

  const markedPerches = centerEl.querySelectorAll(`[${COMPANION_SURFACE_ATTR}="perch"]`);
  for (const el of markedPerches) {
    const rect = rectOf(el);
    if (rect) perches.push(rect);
  }

  const selfEl = document.querySelector(`[${COMPANION_SELF_ATTR}]`);
  const discovered = discoverSurfaces({ root: centerEl, petWidth, now, exclude: selfEl });
  for (const rect of discovered) perches.push(rect);

  const sidebarEl = document.querySelector(COMPANION_SLOTS.sidebar);
  const sidebarRect = sidebarEl ? rectOf(sidebarEl) : null;
  const sidebarEdge = sidebarRect
    ? { x: sidebarRect.right, top: sidebarRect.top, bottom: sidebarRect.bottom }
    : null;

  return { content, petWidth, perches, sidebarEdge, favoured };
}

/**
 * The companion paints above the rest of the app, so wherever it stands it
 * also covers whatever native or manual window-drag trigger is beneath it.
 * Checked at the point the user grabs it, so a drag gesture there moves the
 * window instead of the sprite.
 */
export function isOverDragRegion(clientX: number, clientY: number, self: Element): boolean {
  if (typeof document.elementsFromPoint !== 'function') return false;
  const stack = document.elementsFromPoint(clientX, clientY);
  return stack.some((el) => el !== self && !self.contains(el) && el.closest('[data-tauri-drag-region]'));
}

/**
 * Mark discovered geometry stale whenever the content region actually moves.
 *
 * Discovery used to rescan on a two-second timer, which paid for a full sweep
 * on a page that had not changed and still left the companion standing on
 * stale rects for up to that long after one that had. Observing the layout
 * inverts both: a quiet page never rescans, and a reflow is picked up on the
 * next planning beat.
 *
 * Both observers are cheap here because they only set a flag — the scan itself
 * still happens on the companion's own schedule, never in the observer
 * callback, so a burst of mutations cannot turn into a burst of layout reads.
 *
 * Returns a teardown. Degrades to the TTL backstop where the observers are
 * unavailable.
 */
/**
 * Mark discovered surfaces stale when the chrome moves.
 *
 * `onChange` lets a caller react to the staleness rather than wait for its own
 * schedule: the companion can be standing on a ledge that has just stopped
 * existing, and the flag is the only prompt notice it gets.
 */
export function watchLayout(root: Element, onChange?: () => void): () => void {
  const stops: Array<() => void> = [];
  const invalidate = () => { invalidateDiscovery(); onChange?.(); };

  if (typeof ResizeObserver !== 'undefined') {
    const ro = new ResizeObserver(invalidate);
    ro.observe(root);
    stops.push(() => ro.disconnect());
  }

  if (typeof MutationObserver !== 'undefined') {
    // Only element churn can move a surface. Typing rewrites text nodes
    // continuously, and treating that as a layout change would rescan on
    // every keystroke — busier than the timer this replaced, in exactly the
    // case that matters.
    const movesSomething = (records: MutationRecord[]) => records.some((r) => (
      [...r.addedNodes, ...r.removedNodes].some((n) => n.nodeType === Node.ELEMENT_NODE)
    ));
    const mo = new MutationObserver((records) => {
      if (movesSomething(records)) invalidate();
    });
    mo.observe(root, { childList: true, subtree: true });
    stops.push(() => mo.disconnect());
  }

  return () => { for (const stop of stops) stop(); };
}
